import { inject, Injectable } from '@angular/core';
import { KeyValueStorageInterface } from 'aws-amplify/utils';

import {
  DEVICE_KEY_SUFFIXES,
  isDurableKey,
  LAST_AUTH_USER_SUFFIX,
  REQUIRED_SESSION_KEY_SUFFIXES,
  VAULT,
} from './vault';

/**
 * The storage Amplify writes its Cognito tokens through.
 *
 * Amplify never exposes the Cognito refresh token — `AuthTokens` is only the ID and access
 * tokens — so the app cannot hold the Durable Root itself. What it can do is own the storage
 * Amplify persists to, by handing this to `cognitoUserPoolsTokenProvider.setKeyValueStorage()`.
 *
 * Three jobs:
 *
 * 1. **Route by key.** Only the six keys Amplify needs back to refresh reach the Vault — see
 *    `DURABLE_KEY_PATTERN`, which also records the invariant this preserves. The ID token stays in
 *    memory and is re-minted on the next launch.
 * 2. **Read from memory.** Amplify reads its storage often and at times we do not choose, and a
 *    native read on each one is a round trip. Memory is filled once by `prime()` during Session
 *    Restore, and every read after that is served from it.
 * 3. **Write only when something changed.** See {@link setItem} — Amplify rewrites its whole token
 *    set on every refresh, and with rotation off most of those writes store what is already there.
 *
 * The second job is a deliberate, documented trade: after priming, the Durable Root is held in
 * memory for the rest of the launch. The Vault protects it at rest, not from the running process.
 */
@Injectable()
export class VaultTokenStorage implements KeyValueStorageInterface {
  private readonly vault = inject(VAULT);

  /**
   * An ECMAScript private field, not a TypeScript `private` one.
   *
   * `private` is erased at compile time: the property would still be readable as
   * `storage.memory` by anything holding the instance, and would be picked up by anything that
   * serialises the object into a log or error context. `#memory` is genuinely unreachable from
   * outside the class and is skipped by `JSON.stringify`.
   *
   * This is not a security boundary against code running in the process — JavaScript has no
   * memory isolation, and a determined attacker can monkey-patch the methods below. It closes
   * the accidental paths: a logger that serialises too eagerly, or unrelated code that injects
   * this service and reads a field it should not know about.
   */
  readonly #memory = new Map<string, string>();

  /**
   * Fill memory from the Vault, once per launch, so every later read is a memory read.
   *
   * The only load path. It used to have a prompting sibling for a `gated` Vault; the prompt now
   * happens before this is reached and releases nothing, so both launches arrive here identically.
   *
   * Why memory is filled once rather than read through: see the class doc.
   */
  async prime(): Promise<boolean> {
    return this.load(key => this.vault.read(key));
  }

  /**
   * Persist a durable value, unless the Vault already holds exactly this one.
   *
   * The prompt this originally avoided no longer exists — no write is gated. It is kept because it
   * is still correct and still cheap: Amplify rewrites its whole token set on every refresh, and
   * with rotation off the refresh token comes back byte-identical every time, so most of those
   * writes are a native round trip that changes nothing.
   *
   * A skip, not a cache: a changed value still writes, so this stays correct if rotation is ever
   * switched on — it simply stops paying off for the refresh token. Memory is the comparison because
   * it is filled from the Vault by {@link prime} and updated on every write, so the two agree
   * except after {@link forget}, which empties memory and leaves the Vault alone. That direction is
   * safe: an unnecessary write costs a round trip, while a wrongly skipped one would lose the value.
   *
   * **Memory is updated after the write, not before**, which is what keeps that comparison sound: a
   * refused write leaves memory holding the old value, so the retry of the same value still counts as
   * changed and still reaches the Vault. Updating first would record a value the Vault does not hold,
   * and the skip would then suppress the write that could have fixed it.
   */
  async setItem(key: string, value: string): Promise<void> {
    const unchanged = this.#memory.get(key) === value;
    if (isDurableKey(key) && !unchanged) {
      await this.vault.write(key, value);
    }
    this.#memory.set(key, value);
  }

  /** Always served from memory, so Amplify can never provoke a prompt. */
  async getItem(key: string): Promise<string | null> {
    return this.#memory.get(key) ?? null;
  }

  async removeItem(key: string): Promise<void> {
    this.#memory.delete(key);
    if (isDurableKey(key)) {
      await this.vault.remove(key);
    }
  }

  /**
   * The Sign-out path: memory and Vault both, and a rejection if the Vault kept anything.
   *
   * Memory is emptied first and unconditionally, so Amplify cannot see a session even on a device
   * that will not give its own up. What the rejection then says is the part that has to survive: the
   * Durable Root is still there. Built on {@link Vault.purge} rather than a loop over `keys` and
   * `remove` because those two swallow their failures, and a clear that cannot fail is a clear
   * nobody can trust.
   */
  async clear(): Promise<void> {
    this.#memory.clear();
    await this.vault.purge();
  }

  /**
   * Drop Amplify's view of the session without touching the Vault.
   *
   * The narrow counterpart to {@link clear}: this one is not a Sign-out. Amplify refuses to sign
   * anyone in while it can see a session, and
   * the only API it offers for that is `signOut()` — which removes its refresh token through
   * {@link removeItem} and revokes it. Using that to make room for a password attempt would destroy
   * a good Durable Root before knowing whether the password was even right.
   *
   * Forgetting the memory instead leaves the Vault as the durable truth: a wrong password or an
   * unreachable server costs nothing, and a correct one writes a new Durable Root over the old.
   */
  forget(): void {
    this.#memory.clear();
  }

  private async durableKeys(): Promise<readonly string[]> {
    return (await this.vault.keys()).filter(isDurableKey);
  }

  private async load(read: (key: string) => Promise<string | null>): Promise<boolean> {
    for (const key of await this.durableKeys()) {
      const value = await read(key);
      if (value !== null) this.#memory.set(key, value);
    }
    return this.hasSession();
  }

  /**
   * Whether what was just loaded is a session Amplify will actually act on.
   *
   * "Is there a refresh token?" is a weaker question than it looks. Amplify needs its whole required
   * set back before it reports a session, so a Vault holding only a Durable Root would send the
   * machine into a Session Restore that cannot succeed — and the failure would surface two states
   * later, where the only available reading is that the credential had been refused. A
   * `refreshRejected` clears the Vault, so answering this question loosely turns a storage gap into a
   * permanent Forced Sign-out.
   *
   * Saying `false` here routes to `noSession`, which leaves the Vault alone and costs one sign-in.
   */
  private hasSession(): boolean {
    const prefix = this.sessionPrefix();
    if (prefix === null) return false;

    const present = (suffix: string): boolean => this.#memory.has(prefix + suffix);

    if (!REQUIRED_SESSION_KEY_SUFFIXES.every(present)) return false;

    // All three or none: a partial trio is the gap that would otherwise reach Cognito.
    const deviceKeys = DEVICE_KEY_SUFFIXES.filter(present);
    return deviceKeys.length === 0 || deviceKeys.length === DEVICE_KEY_SUFFIXES.length;
  }

  /**
   * The prefix Amplify will read this launch, or `null` when nothing names one.
   *
   * Rebuilt the way Amplify rebuilds it — the `LastAuthUser` key without its suffix, then that key's
   * *value* — so every question above is asked about one person's keys. Asking by suffix across the
   * whole store instead is what lets a device that has held two sessions answer yes with a set
   * Amplify cannot load: see {@link REQUIRED_SESSION_KEY_SUFFIXES} for why two prefixes is the
   * ordinary state of such a device rather than a damaged one.
   *
   * An empty value is no name at all. Amplify would derive a prefix ending in a bare `.` and look
   * for keys nothing ever wrote.
   */
  private sessionPrefix(): string | null {
    for (const [key, value] of this.#memory) {
      if (key.endsWith(LAST_AUTH_USER_SUFFIX) && value !== '') {
        return `${key.slice(0, -LAST_AUTH_USER_SUFFIX.length)}.${value}`;
      }
    }
    return null;
  }
}
